import { getWazzupChannelTypeFamily } from '@pages/Admin/CRMSettings/tabs/GeneralPage/components/WazzupCrmSettings/lib/wazzupCrmIntegration.helpers'
import { WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES } from './crmIntegrationOutgoingAttachment'

/** Семейство канала для правил вложений CRM→Wazzup. */
export type WazzupOutgoingChannelFamily =
  | 'whatsapp'
  | 'waba'
  | 'viber'
  | 'telegram'
  | 'instagram'
  | 'vk'
  | 'max'
  | 'avito'
  | 'cian'
  | 'unknown'

export type WazzupOutgoingAttachmentKind =
  | 'image'
  | 'document'
  | 'video'
  | 'audio'
  | 'voice'

export type WazzupOutgoingChannelRules = {
  family: WazzupOutgoingChannelFamily
  label: string
  /**
   * Расширения без точки, lower-case.
   * Пустой массив + `softExtensionCheck` — бэк не режет формат (ЦИАН / unknown).
   */
  extensions: readonly string[]
  /** Не отклонять файл по расширению (только размер). */
  softExtensionCheck: boolean
  imageMaxBytes: number
  /** Документы / прочие файлы (не image/video/audio). */
  documentMaxBytes: number
  videoMaxBytes: number
  audioMaxBytes: number
  voiceMaxBytes: number
  allowVoiceRecorder: boolean
  /** Текст тултипа для UI. */
  hint: string
}

const MB = 1024 * 1024

/** CRM / API send: потолок 10 МБ на любое вложение (кроме каналов с меньшим channelMax). */
const crmCap = (channelLimitBytes: number): number =>
  Math.min(channelLimitBytes, WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES)

const WA_EXT = [
  'pdf',
  'zip',
  'gif',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'jpg',
  'jpeg',
  'png',
  'aac',
  'mp3',
  'amr',
  'mpeg',
  'mp4',
  'ogg',
] as const

const WABA_EXT = [
  'pdf',
  'zip',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'jpg',
  'jpeg',
  'png',
  'mp4',
  '3gpp',
  'aac',
  'mp3',
  'amr',
  'm4a',
  'ogg',
] as const

const TG_EXT = [
  'pdf',
  'zip',
  'gif',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'jpg',
  'jpeg',
  'png',
  'mp3',
  'm4a',
  'ogg',
  'mp4',
] as const

const VK_EXT = [
  'pdf',
  'zip',
  'gif',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'jpg',
  'jpeg',
  'png',
  'mp3',
] as const

const VIBER_EXT = [
  'pdf',
  'zip',
  'gif',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'txt',
  'jpg',
  'jpeg',
  'png',
  'aac',
  'mp3',
  'amr',
  'mpeg',
  'mp4',
  'm4a',
] as const

const MAX_EXT = [
  'pdf',
  'zip',
  'gif',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'jpg',
  'jpeg',
  'png',
  'mp4',
] as const

const IG_EXT = ['jpg', 'jpeg', 'png', 'bmp'] as const

const AVITO_EXT = ['jpg', 'jpeg', 'png', 'mp4'] as const

const IMAGE_EXTS = new Set([
  'jpg',
  'jpeg',
  'png',
  'bmp',
  'gif',
  'webp',
])
const VIDEO_EXTS = new Set(['mp4', '3gpp', 'mov', 'webm', 'avi'])
const AUDIO_EXTS = new Set([
  'aac',
  'mp3',
  'amr',
  'mpeg',
  'ogg',
  'm4a',
  'wav',
])

/** Voice разрешён, если в whitelist есть mp3 или ogg (бэк конвертит). */
const voiceAllowedByExt = (exts: readonly string[]): boolean =>
  exts.includes('mp3') || exts.includes('ogg')

export const resolveWazzupOutgoingChannelFamily = (
  channelType: string | null | undefined,
): WazzupOutgoingChannelFamily => {
  const family = getWazzupChannelTypeFamily(String(channelType ?? ''))
  const normalized = String(channelType ?? '')
    .trim()
    .toLowerCase()

  // whatsapp / wapi → WhatsApp; waba отдельно.
  if (normalized === 'waba') return 'waba'
  if (family === 'whatsapp' || normalized === 'whatsapp' || normalized === 'wapi') {
    return 'whatsapp'
  }
  if (family === 'telegram' || normalized === 'telegram' || normalized === 'tgapi') {
    return 'telegram'
  }
  if (
    family === 'max' ||
    normalized === 'max' ||
    normalized === 'maxbot' ||
    normalized === 'maxgroup'
  ) {
    return 'max'
  }
  if (family === 'vk' || normalized === 'vk' || normalized === 'vkontakte') {
    return 'vk'
  }
  if (normalized === 'viber') return 'viber'
  if (normalized === 'instagram' || normalized === 'ig') return 'instagram'
  if (normalized === 'avito') return 'avito'
  if (normalized === 'cian' || normalized === 'cian.ru') return 'cian'
  return 'unknown'
}

const RULES_BY_FAMILY: Record<
  WazzupOutgoingChannelFamily,
  WazzupOutgoingChannelRules
> = {
  whatsapp: {
    family: 'whatsapp',
    label: 'WhatsApp',
    extensions: WA_EXT,
    softExtensionCheck: false,
    imageMaxBytes: crmCap(45 * MB),
    documentMaxBytes: crmCap(45 * MB),
    videoMaxBytes: crmCap(45 * MB),
    audioMaxBytes: crmCap(45 * MB),
    voiceMaxBytes: crmCap(45 * MB),
    allowVoiceRecorder: voiceAllowedByExt(WA_EXT),
    hint: 'PDF, ZIP, GIF, Office, JPG/PNG, AAC/MP3/AMR/MPEG/MP4/OGG. 1 файл, до 10 МБ.',
  },
  waba: {
    family: 'waba',
    label: 'WABA',
    extensions: WABA_EXT,
    softExtensionCheck: false,
    imageMaxBytes: crmCap(5 * MB),
    documentMaxBytes: crmCap(50 * MB),
    videoMaxBytes: crmCap(16 * MB),
    audioMaxBytes: crmCap(16 * MB),
    voiceMaxBytes: crmCap(16 * MB),
    allowVoiceRecorder: voiceAllowedByExt(WABA_EXT),
    hint: 'PDF, ZIP, DOCX, XLS/XLSX, PPT/PPTX, JPG/PNG, MP4/3GPP, AAC/MP3/AMR/M4A/OGG. Без DOC и GIF. 1 файл; фото до 5 МБ, остальное до 10 МБ.',
  },
  telegram: {
    family: 'telegram',
    label: 'Telegram (Wazzup)',
    extensions: TG_EXT,
    softExtensionCheck: false,
    imageMaxBytes: crmCap(5 * MB),
    documentMaxBytes: crmCap(20 * MB),
    videoMaxBytes: crmCap(20 * MB),
    audioMaxBytes: crmCap(20 * MB),
    voiceMaxBytes: crmCap(20 * MB),
    allowVoiceRecorder: voiceAllowedByExt(TG_EXT),
    hint: 'PDF, ZIP, GIF, Office, JPG/PNG, MP3/M4A/OGG, MP4. 1 файл; фото до 5 МБ, остальное до 10 МБ.',
  },
  vk: {
    family: 'vk',
    label: 'ВКонтакте (Wazzup)',
    extensions: VK_EXT,
    softExtensionCheck: false,
    imageMaxBytes: crmCap(50 * MB),
    documentMaxBytes: crmCap(50 * MB),
    videoMaxBytes: 0,
    audioMaxBytes: crmCap(50 * MB),
    voiceMaxBytes: crmCap(50 * MB),
    allowVoiceRecorder: voiceAllowedByExt(VK_EXT),
    hint: 'PDF, ZIP, GIF, Office, JPG/PNG, MP3. Без видео. 1 файл, до 10 МБ. Голосовое — да (MP3).',
  },
  viber: {
    family: 'viber',
    label: 'Viber',
    extensions: VIBER_EXT,
    softExtensionCheck: false,
    imageMaxBytes: crmCap(50 * MB),
    documentMaxBytes: crmCap(50 * MB),
    videoMaxBytes: crmCap(50 * MB),
    audioMaxBytes: crmCap(50 * MB),
    voiceMaxBytes: crmCap(50 * MB),
    allowVoiceRecorder: voiceAllowedByExt(VIBER_EXT),
    hint: 'PDF, ZIP, GIF, Office, TXT, JPG/PNG, AAC/MP3/AMR/MPEG/MP4/M4A. 1 файл, до 10 МБ.',
  },
  max: {
    family: 'max',
    label: 'MAX (Wazzup)',
    extensions: MAX_EXT,
    softExtensionCheck: false,
    imageMaxBytes: crmCap(50 * MB),
    documentMaxBytes: crmCap(50 * MB),
    videoMaxBytes: crmCap(50 * MB),
    audioMaxBytes: 0,
    voiceMaxBytes: 0,
    allowVoiceRecorder: false,
    hint: 'PDF, ZIP, GIF, Office, JPG/PNG, MP4. Без MP3/OGG и голосовых. 1 файл, до 10 МБ.',
  },
  instagram: {
    family: 'instagram',
    label: 'Instagram',
    extensions: IG_EXT,
    softExtensionCheck: false,
    imageMaxBytes: crmCap(8 * MB),
    documentMaxBytes: 0,
    videoMaxBytes: 0,
    audioMaxBytes: 0,
    voiceMaxBytes: 0,
    allowVoiceRecorder: false,
    hint: 'Только JPG, JPEG, PNG, BMP. 1 файл, до 8 МБ. Без документов, видео и голосовых.',
  },
  avito: {
    family: 'avito',
    label: 'Авито',
    extensions: AVITO_EXT,
    softExtensionCheck: false,
    imageMaxBytes: crmCap(50 * MB),
    documentMaxBytes: 0,
    videoMaxBytes: crmCap(50 * MB),
    audioMaxBytes: 0,
    voiceMaxBytes: 0,
    allowVoiceRecorder: false,
    hint: 'JPG, JPEG, PNG или MP4. 1 файл, до 10 МБ. Без документов и голосовых.',
  },
  cian: {
    family: 'cian',
    label: 'Циан',
    extensions: [],
    softExtensionCheck: true,
    imageMaxBytes: crmCap(50 * MB),
    documentMaxBytes: crmCap(50 * MB),
    videoMaxBytes: crmCap(50 * MB),
    audioMaxBytes: crmCap(50 * MB),
    voiceMaxBytes: 0,
    allowVoiceRecorder: false,
    hint: 'Любой формат, 1 файл, до 10 МБ. Голосовые не поддерживаются.',
  },
  /** Неизвестный канал — не режем расширение; лимит 10 МБ. */
  unknown: {
    family: 'unknown',
    label: 'Wazzup',
    extensions: [],
    softExtensionCheck: true,
    imageMaxBytes: WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES,
    documentMaxBytes: WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES,
    videoMaxBytes: WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES,
    audioMaxBytes: WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES,
    voiceMaxBytes: 0,
    allowVoiceRecorder: false,
    hint: '1 файл, до 10 МБ. Формат зависит от канала.',
  },
}

export const getWazzupOutgoingChannelRules = (
  channelType: string | null | undefined,
): WazzupOutgoingChannelRules =>
  RULES_BY_FAMILY[resolveWazzupOutgoingChannelFamily(channelType)]

const getFileExtension = (fileName: string): string => {
  const match = fileName.trim().match(/\.([^.]+)$/i)
  return match ? match[1].toLowerCase() : ''
}

const formatMb = (bytes: number): string => {
  if (bytes < MB) {
    return `${Math.max(1, Math.round(bytes / 1024))} КБ`
  }
  const mb = bytes / MB
  return Number.isInteger(mb) ? `${mb} МБ` : `${mb.toFixed(1)} МБ`
}

const formatExtList = (exts: readonly string[]): string =>
  exts.map((ext) => ext.toUpperCase()).join(', ')

const isExtensionAllowed = (
  ext: string,
  rules: WazzupOutgoingChannelRules,
): boolean => {
  if (rules.softExtensionCheck) return true
  if (!ext) return false
  return rules.extensions.includes(ext)
}

export const inferWazzupOutgoingAttachmentKind = (
  file: File,
  rules: WazzupOutgoingChannelRules,
  options?: { forceVoice?: boolean },
): WazzupOutgoingAttachmentKind | null => {
  if (options?.forceVoice) {
    return rules.allowVoiceRecorder ? 'voice' : null
  }

  const ext = getFileExtension(file.name)
  const mime = (file.type || '').toLowerCase()

  if (!isExtensionAllowed(ext, rules) && !rules.softExtensionCheck) {
    return null
  }

  if (IMAGE_EXTS.has(ext) || mime.startsWith('image/')) {
    // GIF в whitelist каналов — как документ (не photo), кроме Instagram/Avito где gif нет.
    if (ext === 'gif') {
      return rules.extensions.includes('gif') || rules.softExtensionCheck
        ? 'document'
        : null
    }
    if (rules.imageMaxBytes <= 0 && !rules.softExtensionCheck) {
      return null
    }
    return 'image'
  }

  if (VIDEO_EXTS.has(ext) || mime.startsWith('video/')) {
    if (rules.videoMaxBytes <= 0 && !rules.softExtensionCheck) {
      return null
    }
    return 'video'
  }

  if (AUDIO_EXTS.has(ext) || mime.startsWith('audio/')) {
    if (rules.audioMaxBytes <= 0 && !rules.softExtensionCheck) {
      return null
    }
    return 'audio'
  }

  if (rules.documentMaxBytes <= 0 && !rules.softExtensionCheck) {
    return null
  }
  return 'document'
}

const maxBytesForKind = (
  rules: WazzupOutgoingChannelRules,
  kind: WazzupOutgoingAttachmentKind,
): number => {
  switch (kind) {
    case 'image':
      return rules.imageMaxBytes
    case 'document':
      return rules.documentMaxBytes
    case 'video':
      return rules.videoMaxBytes
    case 'audio':
      return rules.audioMaxBytes
    case 'voice':
      return rules.voiceMaxBytes
    default:
      return WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES
  }
}

/** null = ок; иначе текст ошибки для тоста. */
export const validateWazzupOutgoingAttachment = (
  file: File,
  channelType: string | null | undefined,
  options?: { forceVoice?: boolean },
): string | null => {
  const rules = getWazzupOutgoingChannelRules(channelType)

  if (options?.forceVoice) {
    if (!rules.allowVoiceRecorder || rules.voiceMaxBytes <= 0) {
      return `Для канала ${rules.label} голосовые сообщения не поддерживаются`
    }
    if (file.size > rules.voiceMaxBytes) {
      return `Для ${rules.label} размер голосового не должен превышать ${formatMb(
        rules.voiceMaxBytes,
      )}`
    }
    if (file.size > WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES) {
      return `Размер вложения для Wazzup не должен превышать ${formatMb(
        WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES,
      )}`
    }
    return null
  }

  const ext = getFileExtension(file.name)
  if (!rules.softExtensionCheck && !isExtensionAllowed(ext, rules)) {
    if (rules.extensions.length === 0) {
      return `Для канала ${rules.label} вложения не поддерживаются`
    }
    return `Для канала ${rules.label} доступны: ${formatExtList(rules.extensions)}`
  }

  const kind = inferWazzupOutgoingAttachmentKind(file, rules)
  if (!kind) {
    if (rules.extensions.length === 0 && !rules.softExtensionCheck) {
      return `Для канала ${rules.label} вложения не поддерживаются`
    }
    return `Для канала ${rules.label} этот тип файла не поддерживается`
  }

  const maxBytes = maxBytesForKind(rules, kind)
  if (maxBytes <= 0) {
    return `Для канала ${rules.label} этот тип вложения не поддерживается`
  }

  if (file.size > maxBytes) {
    return `Для ${rules.label} размер ${
      kind === 'image'
        ? 'изображения'
        : kind === 'video'
          ? 'видео'
          : kind === 'audio'
            ? 'аудио'
            : 'файла'
    } не должен превышать ${formatMb(maxBytes)}`
  }

  if (file.size > WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES) {
    return `Размер вложения для Wazzup не должен превышать ${formatMb(
      WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES,
    )}`
  }

  return null
}

export const getWazzupOutgoingFileAccept = (
  channelType: string | null | undefined,
): string => {
  const rules = getWazzupOutgoingChannelRules(channelType)
  if (rules.softExtensionCheck || rules.extensions.length === 0) {
    return ''
  }
  return Array.from(new Set(rules.extensions))
    .map((ext) => `.${ext}`)
    .join(',')
}

export const getWazzupOutgoingAttachmentsHint = (
  channelType: string | null | undefined,
): string => getWazzupOutgoingChannelRules(channelType).hint

export const isWazzupOutgoingVoiceRecorderAllowed = (
  channelType: string | null | undefined,
): boolean => getWazzupOutgoingChannelRules(channelType).allowVoiceRecorder

export const getWazzupOutgoingVoiceMaxBytes = (
  channelType: string | null | undefined,
): number => getWazzupOutgoingChannelRules(channelType).voiceMaxBytes

/** Wazzup: строго 1 вложение на сообщение. */
export const WAZZUP_OUTGOING_MAX_ATTACHMENTS = 1
